/**
 * @file SessionsFilters.tsx
 * @brief Filter bar for the Sessions table — search + status + kind +
 *        date range + user picker + company picker.
 *
 * @details
 * Flush `GridCells` layout matched to Users/Companies filters (29-07-2026).
 * Date range uses `DateRangePicker`; `from`/`to` are ISO day bounds.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — end-to-end GridCell filters
 * @updated 01-10-2026: status / kind / user / company became header pills
 *          (`SessionsHeaderFilters`), the date the page header's circle; no
 *          Clear button. This file's bar is the search.
 */

import { useEffect, useState } from "react";
import { HeaderFilterMenu } from "@/components/ui/HeaderFilterMenu";
import { ExpandingSearch } from "@/components/ui/ExpandingSearch";
import type { SearchableMenuItem } from "@/components/ui/SearchableMenuList";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { CompanyPicker } from "@/features/companies/components/CompanyPicker";
import { UserPicker } from "@/features/users/components/UserPicker";
import type { AdminSessionsListParams } from "../schemas";

const ALL = "all";

const STATUS_ITEMS: SearchableMenuItem[] = [
  { value: ALL, label: "Any status" },
  { value: "completed", label: "Completed" },
  { value: "in_progress", label: "In progress" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];

const KIND_ITEMS: SearchableMenuItem[] = [
  { value: ALL, label: "Any kind" },
  { value: "standard", label: "Standard" },
  { value: "realtime", label: "Realtime" },
];

type StatusFilter = NonNullable<AdminSessionsListParams["status"]> | typeof ALL;
type KindFilter = NonNullable<AdminSessionsListParams["kind"]> | typeof ALL;

interface SessionsFiltersProps {
  value: AdminSessionsListParams;
  onChange: (next: AdminSessionsListParams) => void;
}

const labelOf = (items: SearchableMenuItem[], v: string) =>
  items.find((i) => i.value === v)?.label ?? items[0].label;

/** Status, kind, user and company as the page header's text pills. */
export function SessionsHeaderFilters({ value, onChange }: SessionsFiltersProps) {
  const status: StatusFilter = (value.status ?? ALL) as StatusFilter;
  const kind: KindFilter = (value.kind ?? ALL) as KindFilter;

  return (
    <>
      <HeaderFilterMenu
        title="Filter by status"
        searchPlaceholder="Search statuses"
        items={STATUS_ITEMS}
        selected={status}
        label={labelOf(STATUS_ITEMS, status)}
        active={status !== ALL}
        onClear={() => onChange({ ...value, status: undefined, page: 1 })}
        onSelect={(v) =>
          onChange({
            ...value,
            status: v === ALL ? undefined : (v as AdminSessionsListParams["status"]),
            page: 1,
          })
        }
      />
      <HeaderFilterMenu
        title="Filter by kind"
        searchPlaceholder="Search kinds"
        items={KIND_ITEMS}
        selected={kind}
        label={labelOf(KIND_ITEMS, kind)}
        active={kind !== ALL}
        onClear={() => onChange({ ...value, kind: undefined, page: 1 })}
        onSelect={(v) =>
          onChange({
            ...value,
            kind: v === ALL ? undefined : (v as AdminSessionsListParams["kind"]),
            page: 1,
          })
        }
      />
      <UserPicker
        variant="pill"
        value={value.user_id ?? null}
        onChange={(id) => onChange({ ...value, user_id: id ?? undefined, page: 1 })}
        placeholder="Any user"
      />
      <CompanyPicker
        variant="pill"
        value={value.company_id ?? null}
        onChange={(id) => onChange({ ...value, company_id: id ?? undefined, page: 1 })}
        placeholder="Any company"
      />
    </>
  );
}

/** The search, rendered by the page inside its bottom dock (DES 01-10-2026). */
export function SessionsFilters({ value, onChange }: SessionsFiltersProps) {
  const [searchInput, setSearchInput] = useState(value.search ?? "");
  const debouncedSearch = useDebouncedValue(searchInput, 350);

  useEffect(() => {
    if ((value.search ?? "") === debouncedSearch) return;
    onChange({ ...value, search: debouncedSearch || undefined, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  return (
    // The Notes screen's search (DES 01-10-2026), rendered by the page
    // inside its bottom PageDock ("dock style ki tarha neechay").
    <ExpandingSearch
      value={searchInput}
      onChange={(next) => setSearchInput(next)}
      placeholder="Search by session ID, name, email, company, reference"
      label="Search sessions"
    />
  );
}
